import { SVGAssets } from '@business/assets/vectors';
import React, { useState, useCallback, useRef, useEffect, FC } from 'react';
import { useDropzone } from 'react-dropzone';
import { validateImageFile } from '@utils/helpers/s3Upload';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';

interface UploadPhotoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload?: (files: File[]) => void;
}

// Extend File to include preview property
type FileWithPreview = File & { preview: string };

export const FilesUploadModal: FC<UploadPhotoModalProps> = ({
  isOpen,
  onClose,
  onUpload
}) => {
  const { t } = useTranslation();

  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [error, setError] = useState<string>('');

  const modalRef = useRef<HTMLDivElement>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setError('');

    // Validate and add all files
    const validFiles: FileWithPreview[] = [];
    acceptedFiles.forEach(file => {
      const validation = validateImageFile(file); // 50MB limit for videos
      if (!validation.valid) {
        setError(validation.error || 'Invalid file');
      } else {
        const fileWithPreview = Object.assign(file, {
          preview: file.type.startsWith('video/')
            ? URL.createObjectURL(file)
            : URL.createObjectURL(file)
        }) as FileWithPreview;
        validFiles.push(fileWithPreview);
      }
    });

    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': [],
      'video/*': ['.mp4', '.mov', '.avi', '.mkv', '.webm']
    },
    multiple: true // Change to true for multiple files
  });

  const handleRemoveFile = (fileName: string) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.name === fileName);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.name !== fileName);
    });
  };

  const handleUpload = () => {
    if (files.length === 0) {
      setError('Please select at least one file');
      return;
    }

    // Call the onUpload prop with the files
    if (onUpload) {
      onUpload(files);
      // Clear files after upload
      setFiles([]);
      setError('');
      onClose();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      // Clear files when modal is closed
      setFiles([]);
      setError('');
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Cleanup previews
      files.forEach(file => URL.revokeObjectURL(file.preview));
    };
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
        <div
          ref={modalRef}
          className="bg-white rounded-2xl p-6 shadow-lg relative w-[480px]">
          <Button
            btnStyle="link"
            className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 text-2xl disabled:opacity-50"
            onClick={() => {
              setFiles([]);
              onClose();
            }}>
            <SVGAssets.CloseIconGray className="w-3 h-3" />
          </Button>

          <h3 className="text-lg font-semibold text-[#353535] mb-2">
            {t('dashboard.uploadFiles')}
          </h3>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          {/* Upload Section */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
    max-h-[300px] overflow-y-auto
    ${isDragActive ? 'border-green-500 bg-green-50' : 'border-gray-300'}
  `}>
            <input {...getInputProps()} />
            <div className="flex flex-col items-center justify-center text-[#353535]">
              {files.length === 0 ? (
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="flex items-center justify-center mb-3">
                    <SVGAssets.ModalUploadIcon className="w-14 h-14" />
                  </div>
                  <p className="font-medium">{t('Upload your files here')}</p>
                  <p className="text-sm text-gray-500">
                    {t('Drag images or videos here or')}
                  </p>
                </div>
              ) : (
                <div className="mt-4 w-full">
                  <div className="grid grid-cols-3 gap-3">
                    {files.map(file => (
                      <div key={file.name} className="relative">
                        {file.type.startsWith('video/') ? (
                          <video
                            src={file.preview}
                            className="rounded-lg h-24 w-24 object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={file.preview}
                            alt="preview"
                            className="rounded-lg h-24 w-24 object-cover"
                          />
                        )}
                        <Button
                          btnStyle="link"
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            handleRemoveFile(file.name);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600">
                          <SVGAssets.CloseIconGray className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <SVGAssets.AddRoundIcon className="w-4 h-4" />
                <p className="text-[#7F9D48] font-medium underline mt-1">
                  {t('Browse files to upload')}
                </p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-between mt-6">
            <Button
              onClick={() => {
                setFiles([]);
                onClose();
              }}
              className="cancel-btn">
              {t('cancel')}
            </Button>
            <Button
              btnStyle="primary"
              onClick={handleUpload}
              disabled={files.length === 0}
              className="bg-[#7F9D48] text-white font-medium px-5 py-2 rounded-md hover:bg-[#6d8c3e] transition disabled:opacity-50 disabled:cursor-not-allowed">
              {t('upload')}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};
